import { DialogActions } from "@material-ui/core";
import React, { Component } from "react";
import styled from "styled-components";

import { Button } from "../../../Components/Button";
import AppController from "../../../core/AppController";
import { ds } from "../../../core/models/dataService";
import { Presentation } from "../../../core/models/presentation";
import { Slide } from "../../../core/models/slide";
import { Theme } from "../../../core/models/theme";
import { trackActivity } from "../../../core/utilities/utilities";
import { app } from "../../../namespaces";
import CanvasPreview from "../../../react/components/CanvasPreview";
import { BeautifulDialog, DialogContent } from "../../../react/components/Dialogs/BaseDialog";
import FetchingClickShield from "../../../react/components/FetchingClickShield";
import { Gap10, Gap20 } from "../../../react/components/Gap";
import { FlexBox } from "../../../react/components/LayoutGrid";
import { SimpleLabel } from "../../../react/components/SimpleLabel";
import PresentationEditor from "../../PresentationEditor/PresentationEditor";
import PresentationEditorController, { PanelType } from "../../PresentationEditor/PresentationEditorController";

const Container = styled.div`
    width: 100%;
    height: 100%;
    padding: 30px;
    display: flex;
    gap: 20px;
`;

const PREVIEW_SIZE = 512;

const PreviewContainer = styled.div`
    width: ${PREVIEW_SIZE}px;
    height: ${PREVIEW_SIZE / 1280 * 720}px;
    position: relative;
`;

export class DefaultSlides extends Component {
    constructor(props) {
        super(props);

        let theme = new Theme(props.theme, { disconnected: true, autoLoad: false });
        theme.loadTheme();

        this.state = {
            theme,
            showEditor: false
        };
    }

    get defaultSlides() {
        let { theme } = this.state;

        let defaultSlides = theme.get("defaultSlides") ?? {};
        let title = { template_id: "title", version: 10 };
        let section = { template_id: "section", version: 10 };

        if (defaultSlides.title) {
            title = JSON.parse(defaultSlides.title);
        }
        if (defaultSlides.section) {
            section = JSON.parse(defaultSlides.section);
        }
        return { title, section };
    }

    get isInDialog() {
        return !!this.props.closeDialog;
    }

    componentDidMount() {
        let { defaultSubPane } = this.props;

        if (!defaultSubPane) {
            return;
        }

        if (this.defaultSlides[defaultSubPane]) {
            this.handleEditSlide(this.defaultSlides[defaultSubPane]);
        }
    }

    handleEditSlide = slideModel => {
        let { closeDialog, themeOrPresentationId, presentation } = this.props;

        if (this.isInDialog) {
            // Cannot render the editor when in a dialog, so we navigate to standalone theme editor instead
            AppController.navigateToThemeEditor(presentation?.id ?? themeOrPresentationId, window.location.pathname, "default-slides", slideModel.template_id);
            closeDialog(false);
            return;
        }

        this.setState({
            showEditor: true,
            slideModel
        });
    }

    handleCloseEditor = async slideModel => {
        if (slideModel) {
            let defaultSlides = this.props.theme.defaultSlides ?? {};
            defaultSlides[slideModel.template_id] = JSON.stringify(slideModel);
            await this.props.update({ defaultSlides });
        }

        // reload theme so previews are refreshed
        let theme = new Theme(this.props.theme, { disconnected: true, autoLoad: false });
        theme.loadTheme();
        this.setState({ showEditor: false, theme });
    }

    render() {
        let { update } = this.props;
        let { theme, showEditor, slideModel } = this.state;

        if (showEditor) {
            return (
                <DefaultSlideEditorDialog slideModel={slideModel}
                    theme={theme}
                    update={update}
                    closeDialog={this.handleCloseEditor}
                />
            );
        }

        const { title: titleSlide, section: sectionSlide } = this.defaultSlides;

        return (
            <Container>
                <FlexBox vertical top left>
                    <SimpleLabel margin={0}>
                        Default Title Slide
                    </SimpleLabel>
                    <Gap10 />
                    <PreviewContainer onDoubleClick={() => this.handleEditSlide(titleSlide)}>
                        <CanvasPreview theme={theme} slideModel={titleSlide} scale={PREVIEW_SIZE / 1280} />
                    </PreviewContainer>
                    <Gap20 />
                    <Button onClick={() => this.handleEditSlide(titleSlide)}>Customize Default Title Slide...</Button>
                </FlexBox>
                <FlexBox vertical top left>
                    <SimpleLabel margin={0}>
                        Default Section Slide
                    </SimpleLabel>
                    <Gap10 />
                    <PreviewContainer onDoubleClick={() => this.handleEditSlide(sectionSlide)}>
                        <CanvasPreview theme={theme} slideModel={sectionSlide} scale={PREVIEW_SIZE / 1280} />
                    </PreviewContainer>
                    <Gap20 />
                    <Button onClick={() => this.handleEditSlide(sectionSlide)}>Customize Default Section Slide...</Button>
                </FlexBox>
            </Container>
        );
    }
}

const EditorContainer = styled.div`
    width: 100%;
    height: 100%;
    background: #4b4e55;
    position: relative;
    border: solid 1px #ccc;
`;

const Header = styled.div`
    font-size: 20px;
    padding: 20px 40px;
    font-weight: bold;
`;

class DefaultSlideEditorDialog extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
        };
    }

    componentDidMount() {
        this.initializePresentation();
    }

    async initializePresentation() {
        const { theme, slideModel } = this.props;

        this.dummyPresentation = new Presentation({
            name: "Team Slide",
            userId: app.user.id,
            orgId: AppController.orgId,
            isDummy: true
        }, { disconnected: true, userId: app.user.id });
        this.dummyPresentation.permissions.write = true;
        this.dummyPresentation.permissions.read = true;

        app.themeManager.saveThemeToPresentation(theme, this.dummyPresentation);

        const slide = new Slide(slideModel, {
            userId: app.user.id,
            presentation: this.dummyPresentation
        });
        await slide.load();

        // detach adapter so changes are not saved automatcally until we call slide.saveChanges()
        slide.detachAdapter();

        await this.dummyPresentation.batchShareSlides([slide], { insertIndex: 0, skipUndo: true });

        ds.selection.presentation = this.dummyPresentation;

        await PresentationEditorController.setPresentation(this.dummyPresentation);

        this.setState({ isLoading: false, slide });
    }

    handleSaveChanges = async () => {
        const { slide } = this.state;

        trackActivity("ThemeActions", "EditSlide", null, null, {
            slide_type: slide.get("template_id"),
        });

        this.props.closeDialog({
            layout: slide.get("layout") ?? {},
            states: slide.get("states"),
            animations: slide.get("animations") ?? {},
            template_id: slide.get("template_id"),
            migrationVersion: slide.get("migrationVersion"),
            version: slide.get("version"),
        });
    }

    handleCancelChanges = () => {
        this.props.closeDialog(null);
    }

    render() {
        const { slideModel } = this.props;
        const { isLoading } = this.state;

        return (
            <BeautifulDialog fullScreen closeDialog={this.handleCancelChanges}>
                <Header>Customize {slideModel.template_id.toTitleCase()} Slide</Header>
                <DialogContent style={{ overflow: "hidden", padding: "0px 34px" }}>
                    <EditorContainer>
                        <FetchingClickShield visible={isLoading} backgroundColor="#4b4d55" />
                        {!isLoading && (
                            <PresentationEditor
                                dummyPresentation={this.dummyPresentation}
                                presentationId={this.dummyPresentation.id}
                                isSingleSlideEditor={true}
                                hidePanels={[PanelType.MORE_ACTIONS, PanelType.ASSIGN_SLIDE, PanelType.COMMENTS, PanelType.NOTES]}
                                allowThemeChange={false}
                                allowSharedSlideEditing={true}
                                allowStyleChange={false}
                                allowSwitchSlide={false}
                            />
                        )}
                    </EditorContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.handleCancelChanges}>Cancel</Button>
                    <Button blue onClick={this.handleSaveChanges}>Save Changes</Button>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}

